import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Spinner,
    Card,
    CardHeader,
    CardBody
} from 'reactstrap';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { toast } from 'react-toastify';
import {
    FiTag,
    FiCalendar,
    FiCheck,
    FiCheckCircle,
    FiAlertCircle,
    FiSearch,
    FiEdit2,
    FiChevronUp,
    FiArrowLeft,
    FiInfo
} from 'react-icons/fi';
import {
    FaStamp,
    FaRoute,
    FaBox,
    FaBarcode
} from 'react-icons/fa';
import {
    getClientUrlDataToken,
    postClientUrlWithToken,
    importURL,
    putClientUrlWithToken
} from '../../../Utils/restUtils';
import {
    getCustomers,
    getCustomersRole,
    selectCustStb,
    selectCustStbRole,
    getRouteSelect,
    getRouteSelectRole,
    getTalukSelect,
    getTalukSelectRole,
    viewStb,
    viewStbRole,
    editStb,
    editStbRole,
    getCustomersProduct,
    getCustomersProductRole,
    getCustomerAllProducts,
    getCustomerAllProductsRole
} from '../../../CommonUrl';
import { Breadcrumbs, Btn } from '../../../AbstractElements';
import AddEditRouteModal from '../../Settings/Routes/AddEditRouteModal';
import AddEditTalukModal from '../../Settings/Taluk/AddEditTalukModal';
import Typeahead from '../../../CommonElements/TypeAhead';
import Transparentspinner from '../../Common/Loader/Transparentspinner';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import moment from 'moment';

export const QUARTER_OPTIONS = [
    { value: 'A', label: 'Quarter A' },
    { value: 'B', label: 'Quarter B' },
    { value: 'C', label: 'Quarter C' },
    { value: 'D', label: 'Quarter D' }
];

const STATUS_OPTIONS = [
    { value: 'pending', label: 'Pending' },
    { value: 'calibrated', label: 'Calibrated' },
    { value: 'inspected', label: 'Inspected' }
];

export const ACCOUNT_TYPE_OPTIONS = [
    { label: 'LMS', value: 'LMS' },
    { label: 'GATC', value: 'GATC' }
];

const customSelectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '38px',
        borderColor: state.isFocused ? '#0061cd' : '#dee2e6',
        borderRadius: '6px',
        boxShadow: state.isFocused ? '0 0 0 3px rgba(0, 97, 205, 0.15)' : 'none',
        '&:hover': {
            borderColor: '#0061cd',
        },
        fontSize: '13px',
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '6px',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    }),
    option: (base, state) => ({
        ...base,
        fontSize: '13px',
        backgroundColor: state.isSelected ? '#0061cd' : state.isFocused ? '#f0f4ff' : 'white',
        color: state.isSelected ? 'white' : '#212529',
        '&:active': {
            backgroundColor: '#0061cd',
            color: 'white',
        },
    }),
};

export const getQuarterCode = (q) => {
    if (!q) return '';
    if (typeof q === 'object') q = q.value || q.label || '';
    const str = String(q).trim();
    if (!str) return '';
    if (str.length === 1) return str.toUpperCase();
    const match = str.match(/quarter\s*([A-D])/i) || str.match(/([A-D])/i);
    return match ? match[1].toUpperCase() : str;
};

export const getQuarterDateBounds = (quarterValue) => {
    const qCode = getQuarterCode(quarterValue);
    switch (qCode) {
        case 'A':
            return {
                months: [1, 2, 3],
                label: 'Jan, Feb, Mar'
            };
        case 'B':
            return {
                months: [4, 5, 6],
                label: 'Apr, May, Jun'
            };
        case 'C':
            return {
                months: [7, 8, 9],
                label: 'Jul, Aug, Sep'
            };
        case 'D':
            return {
                months: [10, 11, 12],
                label: 'Oct, Nov, Dec'
            };
        default:
            return {
                months: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
                label: ''
            };
    }
};

// Normalizes DD-MM-YYYY / YYYY-MM-DD / parseable strings to YYYY-MM-DD (input[type=date] format)
export const normalizeDate = (raw) => {
    if (!raw || raw === 'N/A') return '';
    const str = String(raw).trim();
    const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
    if (dmy) {
        const [, d, m, y] = dmy;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    const ymd = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
    if (ymd) {
        const [, y, m, d] = ymd;
        return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
    }
    return moment(str).isValid() ? moment(str).format('YYYY-MM-DD') : '';
};

export const isDateInQuarter = (date, quarter) => {
    if (!date || !quarter) return true;
    const parts = String(date).split('-');
    if (parts.length < 2) return true;
    return getQuarterDateBounds(quarter).months.includes(parseInt(parts[1], 10));
};

const formatDisplayDate = (date) => (date ? moment(date, 'YYYY-MM-DD').format('DD MMM YYYY') : '');

// Visual meta for the product stamping status indicators
const PRODUCT_STATUS_META = {
    pending: { label: 'Pending', color: '#64748b', bg: '#f1f5f9', border: '#cbd5e1', hint: 'No stamping details yet' },
    ready: { label: 'Ready', color: '#0061cd', bg: '#eef4ff', border: '#0061cd', hint: 'Details entered - will be saved' },
    stamped: { label: 'Stamped', color: '#0b7a5a', bg: '#e6f7f1', border: '#0ca678', hint: 'Already stamped' },
    needs_update: { label: 'Needs Update', color: '#b45309', bg: '#fff4e5', border: '#f59f00', hint: 'Stamping details changed or invalid - will be updated on save' }
};

const isProductComplete = (p) => Boolean(
    p.account_type && p.quarter && p.stamping_date && isDateInQuarter(p.stamping_date, p.quarter)
);

const hasProductChanged = (p) => Boolean(p.original) && (
    (p.account_type?.value || '') !== p.original.account_type ||
    (p.quarter?.value || '') !== p.original.quarter ||
    (p.stamping_date || '') !== p.original.stamping_date
);

/**
 * Pending      - never stamped and details incomplete
 * Ready        - never stamped, details complete (will be created on save)
 * Stamped      - existing stamping record, unchanged and valid
 * Needs Update - existing stamping record that was edited or whose date is outside its quarter
 */
const getProductStatus = (p) => {
    if (p.original) {
        if (hasProductChanged(p) || !isProductComplete(p)) return 'needs_update';
        return 'stamped';
    }
    return isProductComplete(p) ? 'ready' : 'pending';
};

const AddEditStampingModal = ({
    onSave,
    disableCustomerAndAccount = false
}) => {
    const location = useLocation();
    const editData = location.state;
    // Customer and service account cannot be changed while editing an existing stamping
    const isEditMode = Boolean(editData?.customer_id);
    const isCustomerLocked = Boolean(isEditMode || disableCustomerAndAccount || editData?.disableCustomerAndAccount || editData?.isCustomerDisabled);
    const isServiceAccountLocked = Boolean(isEditMode || disableCustomerAndAccount || editData?.disableCustomerAndAccount || editData?.isServiceAccountDisabled);
    const authState = useSelector((state) => state.auth);
    const navigate = useNavigate();

    // Customer state
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [customerOptions, setCustomerOptions] = useState([]);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Service Account state
    const [loadingStbs, setLoadingStbs] = useState(false);
    const [stbOptions, setStbOptions] = useState([]);
    const [selectedServiceAccount, setSelectedServiceAccount] = useState(null);

    // Products under selected service account
    const [loadingProducts, setLoadingProducts] = useState(false);
    const [productsList, setProductsList] = useState([]);
    const [productErrors, setProductErrors] = useState({});

    // Bulk / Default Stamping Template controls
    const [defaultQuarter, setDefaultQuarter] = useState(null);
    const [defaultAccountType, setDefaultAccountType] = useState(null);
    const [defaultStampingDate, setDefaultStampingDate] = useState('');

    // Route fields
    const [loadingRoutes, setLoadingRoutes] = useState(false);
    const [routeOptions, setRouteOptions] = useState([]);
    const [selectedRoute, setSelectedRoute] = useState(null);
    const [routeModal, setRouteModal] = useState(false);

    // Taluk fields
    const [loadingTaluks, setLoadingTaluks] = useState(false);
    const [talukOptions, setTalukOptions] = useState([]);
    const [selectedTaluk, setSelectedTaluk] = useState(null);
    const [talukModal, setTalukModal] = useState(false);

    // Additional Fields
    const [remarks, setRemarks] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    // Product list UX state
    const [productSearch, setProductSearch] = useState('');
    const [expandedKeys, setExpandedKeys] = useState({});
    const [isReviewing, setIsReviewing] = useState(false);
    const [saveError, setSaveError] = useState('');
    const submitLockRef = useRef(false);

    // Request tracking: skip repeat edit-mode loads and ignore responses superseded by a newer request
    const editFetchKeyRef = useRef(null);
    const stbRequestRef = useRef(0);
    const productsRequestRef = useRef(0);

    // Fetch master dropdowns on load
    useEffect(() => {
        fetchCustomerOptions();
        fetchRouteOptions();
        fetchTalukOptions();
    }, []);

    // Handle Edit Mode initialization
    useEffect(() => {
        if (!editData) return;

        const custId = editData.customer_id || editData.customerId || editData.raw?.customer_id || editData.raw?.customer?.id;
        const custName = editData.customer_name || editData.raw?.customer_name || editData.raw?.customer?.name;
        const saId = (editData.service_account_id && editData.service_account_id !== 'N/A')
            ? editData.service_account_id
            : (editData.raw?.service_account_id || editData.raw?.service_account?.id || (editData.service_account && editData.service_account !== 'N/A' ? editData.service_account : null) || (editData.stb_no && editData.stb_no !== 'N/A' ? editData.stb_no : null));

        if (custId) {
            const matchedCust = customerOptions.find(c => String(c.value) === String(custId));
            if (matchedCust) {
                setSelectedCustomer(matchedCust);
            } else {
                const fallbackCust = {
                    value: custId,
                    label: custName ? `${custName} (ID: ${custId})` : `Customer #${custId}`,
                    name: custName
                };
                setSelectedCustomer(fallbackCust);
            }
            // This effect re-runs as each master list loads; only hit the API once per customer/account
            const editFetchKey = `${custId}::${saId}`;
            if (editFetchKeyRef.current !== editFetchKey) {
                editFetchKeyRef.current = editFetchKey;
                fetchServiceAccountsForCustomer(custId, saId);
            }
        }

        // Account Type initialization (LMS / GATC)
        const typeVal = editData.stamping_type || editData.account_type || editData.raw?.stamping_type || editData.raw?.service_account?.stamping_type;
        if (typeVal) {
            const matchedType = ACCOUNT_TYPE_OPTIONS.find(t => t.value.toLowerCase() === String(typeVal).toLowerCase() || t.label.toLowerCase() === String(typeVal).toLowerCase());
            if (matchedType) setDefaultAccountType(matchedType);
        }

        // Quarter initialization
        const qVal = editData.quater || editData.quarter || editData.raw?.quater || editData.raw?.quarter;
        if (qVal) {
            const code = getQuarterCode(qVal);
            const matchedQ = QUARTER_OPTIONS.find(q => q.value === code || q.label.toLowerCase() === String(qVal).toLowerCase());
            if (matchedQ) setDefaultQuarter(matchedQ);
        }

        // Date initialization
        const rawDate =
            (editData.date && editData.date !== 'N/A' ? editData.date : null) ||
            (editData.stamping_date && editData.stamping_date !== 'N/A' ? editData.stamping_date : null) ||
            (editData.stamping_dt && editData.stamping_dt !== 'N/A' ? editData.stamping_dt : null) ||
            (editData.raw?.stamping_date && editData.raw.stamping_date !== 'N/A' ? editData.raw.stamping_date : null) ||
            (editData.raw?.stamping_dt && editData.raw.stamping_dt !== 'N/A' ? editData.raw.stamping_dt : null) ||
            (editData.raw?.date && editData.raw.date !== 'N/A' ? editData.raw.date : null);

        if (rawDate) {
            const str = String(rawDate).trim();
            let formattedDate = '';
            const dmy = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})/);
            if (dmy) {
                const [, d, m, y] = dmy;
                formattedDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
            } else {
                const ymd = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})/);
                if (ymd) {
                    const [, y, m, d] = ymd;
                    formattedDate = `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
                } else if (moment(str).isValid()) {
                    formattedDate = moment(str).format('YYYY-MM-DD');
                }
            }
            setDefaultStampingDate(formattedDate);
        }

        // Route initialization
        const rawRoute = editData.route_id || editData.route || editData.raw?.route_id || editData.raw?.route || editData.raw?.route_name || editData.raw?.service_account?.route_name;
        const rVal = typeof rawRoute === 'object' && rawRoute !== null ? (rawRoute.id || rawRoute.route_id || rawRoute.value || rawRoute.name) : rawRoute;
        const rName = typeof rawRoute === 'object' && rawRoute !== null ? (rawRoute.name || rawRoute.label || rawRoute.route_name) : (editData.route_name || editData.raw?.route_name);
        if (rVal || rName) {
            const matchedRoute = routeOptions.find(r =>
                (rVal && (String(r.value) === String(rVal) || String(r.id) === String(rVal) || String(r.route_id) === String(rVal))) ||
                (rName && (r.label?.toLowerCase() === String(rName)?.toLowerCase() || r.name?.toLowerCase() === String(rName)?.toLowerCase()))
            );
            setSelectedRoute(matchedRoute || {
                label: rName || (typeof rVal === 'string' ? rVal : `Route #${rVal}`),
                value: rVal || rName,
                id: rVal,
                route_id: rVal
            });
        }

        // Taluk initialization
        const rawTaluk = editData.taluk_id || editData.taluk || editData.raw?.taluk_id || editData.raw?.taluk || editData.raw?.taluk_name || editData.raw?.service_account?.taluk_name;
        const tVal = typeof rawTaluk === 'object' && rawTaluk !== null ? (rawTaluk.id || rawTaluk.taluk_id || rawTaluk.value || rawTaluk.name) : rawTaluk;
        const tName = typeof rawTaluk === 'object' && rawTaluk !== null ? (rawTaluk.name || rawTaluk.label || rawTaluk.taluk_name) : (editData.taluk_name || editData.raw?.taluk_name);
        if (tVal || tName) {
            const matchedTaluk = talukOptions.find(t =>
                (tVal && (String(t.value) === String(tVal) || String(t.id) === String(tVal) || String(t.taluk_id) === String(tVal))) ||
                (tName && (t.label?.toLowerCase() === String(tName)?.toLowerCase() || t.name?.toLowerCase() === String(tName)?.toLowerCase()))
            );
            setSelectedTaluk(matchedTaluk || {
                label: tName || (typeof tVal === 'string' ? tVal : `Taluk #${tVal}`),
                value: tVal || tName,
                id: tVal,
                taluk_id: tVal
            });
        }

        setRemarks(editData.remarks || editData.raw?.remarks || '');
    }, [editData, customerOptions, routeOptions, talukOptions]);

    // Edit mode: the preselected service account can resolve before the route/taluk lists load,
    // so auto-fill route and taluk from it once both are available (no extra API calls).
    useEffect(() => {
        if (!editData || !selectedServiceAccount) return;
        const matchesOption = (opt, val, idKey) =>
            String(opt.value) === String(val) ||
            String(opt.id) === String(val) ||
            String(opt[idKey]) === String(val) ||
            opt.label?.toLowerCase() === String(val)?.toLowerCase() ||
            opt.name?.toLowerCase() === String(val)?.toLowerCase();

        const saRoute = selectedServiceAccount.route_id || selectedServiceAccount.route || selectedServiceAccount.route_name;
        if (!selectedRoute && saRoute && routeOptions.length > 0) {
            const matchedRoute = routeOptions.find(r => matchesOption(r, saRoute, 'route_id'));
            if (matchedRoute) setSelectedRoute(matchedRoute);
        }
        const saTaluk = selectedServiceAccount.taluk_id || selectedServiceAccount.taluk || selectedServiceAccount.taluk_name;
        if (!selectedTaluk && saTaluk && talukOptions.length > 0) {
            const matchedTaluk = talukOptions.find(t => matchesOption(t, saTaluk, 'taluk_id'));
            if (matchedTaluk) setSelectedTaluk(matchedTaluk);
        }
    }, [editData, selectedServiceAccount, routeOptions, talukOptions]);

    const fetchCustomerOptions = async () => {
        setLoadingCustomers(true);
        try {
            const url = authState?.userRole === 1 ? getCustomers : getCustomersRole;
            const res = await getClientUrlDataToken(authState?.apiUrl, url);
            let rawList = [];
            if (res?.status === true && Array.isArray(res?.data)) {
                rawList = res.data;
            } else if (res?.data && Array.isArray(res.data.data)) {
                rawList = res.data.data;
            }

            const formatted = rawList.map(item => ({
                value: item.id,
                label: item.name ? `${item.name} (${item.mobile || item.address || 'ID: ' + item.id})` : `Customer #${item.id}`,
                name: item.name,
                mobile: item.mobile,
                address: item.address
            }));
            setCustomerOptions(formatted);
            return formatted;
        } catch (err) {
            console.error("Error fetching customer list:", err);
            return [];
        } finally {
            setLoadingCustomers(false);
        }
    };

    const fetchRouteOptions = async (createdRoute = null) => {
        setLoadingRoutes(true);
        try {
            const endpoint = authState?.userRole === 1 ? getRouteSelect : getRouteSelectRole;
            const res = await getClientUrlDataToken(authState?.apiUrl, endpoint);
            let list = [];
            if (Array.isArray(res?.data?.data)) {
                list = res.data.data;
            } else if (Array.isArray(res?.data)) {
                list = res.data;
            } else if (Array.isArray(res)) {
                list = res;
            }
            const options = list.map((item) => ({
                label: item?.name || item?.route_name || item?.label || `Route #${item?.id}`,
                value: item?.id || item?.route_id || item?.value,
                id: item?.id || item?.route_id,
                route_id: item?.id || item?.route_id,
                name: item?.name || item?.route_name,
            }));
            setRouteOptions(options);

            if (createdRoute) {
                const targetName = typeof createdRoute === "string"
                    ? createdRoute
                    : (createdRoute?.name || createdRoute?.route_name || createdRoute?.label || createdRoute?.data?.name || createdRoute?.data?.route_name);
                const targetId = typeof createdRoute === "object" && createdRoute !== null
                    ? (createdRoute?.id || createdRoute?.route_id || createdRoute?.value || createdRoute?.data?.id || createdRoute?.data?.route_id)
                    : (typeof createdRoute === "number" ? createdRoute : null);

                const matched = options.find((opt) =>
                    (targetId && (String(opt.value) === String(targetId) || String(opt.id) === String(targetId) || String(opt.route_id) === String(targetId))) ||
                    (targetName && (opt.label?.toLowerCase() === String(targetName)?.toLowerCase() || opt.name?.toLowerCase() === String(targetName)?.toLowerCase()))
                );

                if (matched) {
                    setSelectedRoute(matched);
                } else if (targetName || targetId) {
                    const newOpt = {
                        label: targetName || `Route #${targetId}`,
                        value: targetId || targetName,
                        id: targetId || targetName,
                        route_id: targetId || targetName,
                        name: targetName || `Route #${targetId}`,
                    };
                    setRouteOptions((prev) => [newOpt, ...prev.filter(p => String(p.value) !== String(newOpt.value))]);
                    setSelectedRoute(newOpt);
                }
            }
            return options;
        } catch (err) {
            console.error("Error fetching route list:", err);
            return [];
        } finally {
            setLoadingRoutes(false);
        }
    };

    const fetchTalukOptions = async (createdTaluk = null) => {
        setLoadingTaluks(true);
        try {
            const endpoint = authState?.userRole === 1 ? getTalukSelect : getTalukSelectRole;
            const res = await getClientUrlDataToken(authState?.apiUrl, endpoint);
            let list = [];
            if (Array.isArray(res?.data?.data)) {
                list = res.data.data;
            } else if (Array.isArray(res?.data)) {
                list = res.data;
            } else if (Array.isArray(res)) {
                list = res;
            }
            const options = list.map((item) => ({
                label: item?.name || item?.taluk_name || item?.label || `Taluk #${item?.id}`,
                value: item?.id || item?.taluk_id,
                id: item?.id || item?.taluk_id,
                taluk_id: item?.id || item?.taluk_id,
                name: item?.name || item?.taluk_name,
            }));
            setTalukOptions(options);

            if (createdTaluk) {
                const targetName = typeof createdTaluk === "string"
                    ? createdTaluk
                    : (createdTaluk?.name || createdTaluk?.taluk_name || createdTaluk?.label || createdTaluk?.data?.name || createdTaluk?.data?.taluk_name);
                const targetId = typeof createdTaluk === "object" && createdTaluk !== null
                    ? (createdTaluk?.id || createdTaluk?.taluk_id || createdTaluk?.value || createdTaluk?.data?.id || createdTaluk?.data?.taluk_id)
                    : (typeof createdTaluk === "number" ? createdTaluk : null);

                const matched = options.find((opt) =>
                    (targetId && (String(opt.value) === String(targetId) || String(opt.id) === String(targetId) || String(opt.taluk_id) === String(targetId))) ||
                    (targetName && (opt.label?.toLowerCase() === String(targetName)?.toLowerCase() || opt.name?.toLowerCase() === String(targetName)?.toLowerCase()))
                );

                if (matched) {
                    setSelectedTaluk(matched);
                } else if (targetName || targetId) {
                    const newOpt = {
                        label: targetName || `Taluk #${targetId}`,
                        value: targetId || targetName,
                        id: targetId || targetName,
                        taluk_id: targetId || targetName,
                        name: targetName || `Taluk #${targetId}`,
                    };
                    setTalukOptions((prev) => [newOpt, ...prev.filter(p => String(p.value) !== String(newOpt.value))]);
                    setSelectedTaluk(newOpt);
                }
            }
            return options;
        } catch (err) {
            console.error("Error fetching taluk list:", err);
            return [];
        } finally {
            setLoadingTaluks(false);
        }
    };

    /**
     * Fetch products associated with the given Service Account.
     * Extracts full product details (name, sku, warranty, serial/machine number).
     */
    const fetchProductsForServiceAccount = useCallback(async (serviceAccountId, customerId, saObject = null) => {
        const requestId = ++productsRequestRef.current;
        const isStale = () => requestId !== productsRequestRef.current;

        if (!serviceAccountId) {
            setProductsList([]);
            setLoadingProducts(false);
            return;
        }

        setLoadingProducts(true);
        setProductErrors({});

        try {
            let candidateProducts = [];
            const custId = customerId || selectedCustomer?.value;

            // 1. Primary: Fetch via /admin/get-customer-all-products?customer_id=...&service_account_id=...
            if (custId && serviceAccountId) {
                const endpoint = authState?.userRole === 1 ? getCustomerAllProducts : getCustomerAllProductsRole;
                const prodRes = await getClientUrlDataToken(
                    authState?.apiUrl,
                    `${endpoint}?customer_id=${custId}&service_account_id=${serviceAccountId}`
                );
                if (isStale()) return;

                if (prodRes?.status === true && Array.isArray(prodRes?.data)) {
                    candidateProducts = prodRes.data;
                } else if (Array.isArray(prodRes?.data?.data)) {
                    candidateProducts = prodRes.data.data;
                } else if (Array.isArray(prodRes?.data?.items)) {
                    candidateProducts = prodRes.data.items;
                } else if (Array.isArray(prodRes?.data?.products)) {
                    candidateProducts = prodRes.data.products;
                } else if (Array.isArray(prodRes?.data)) {
                    candidateProducts = prodRes.data;
                } else if (Array.isArray(prodRes)) {
                    candidateProducts = prodRes;
                }
            }

            // 2. Fallback: Check if the service account object already includes products
            if (candidateProducts.length === 0) {
                if (Array.isArray(saObject?.products) && saObject.products.length > 0) {
                    candidateProducts = saObject.products;
                } else if (Array.isArray(saObject?.items) && saObject.items.length > 0) {
                    candidateProducts = saObject.items;
                }
            }

            // 3. Fallback: Fetch via viewStb endpoint
            if (candidateProducts.length === 0) {
                const stbEndpoint = authState?.userRole === 1 ? viewStb : viewStbRole;
                const viewRes = await getClientUrlDataToken(authState?.apiUrl, `${stbEndpoint}?stb_id=${serviceAccountId}`);
                if (isStale()) return;
                if (viewRes?.status === true) {
                    const stbDetail = viewRes?.data?.data?.[0] || viewRes?.data?.[0] || viewRes?.data;
                    if (Array.isArray(stbDetail?.products) && stbDetail.products.length > 0) {
                        candidateProducts = stbDetail.products;
                    } else if (Array.isArray(stbDetail?.items) && stbDetail.items.length > 0) {
                        candidateProducts = stbDetail.items;
                    }
                }
            }

            // 4. Fallback: fetch via editStb endpoint
            if (candidateProducts.length === 0) {
                const editEndpoint = authState?.userRole === 1 ? editStb : editStbRole;
                const editRes = await getClientUrlDataToken(authState?.apiUrl, `${editEndpoint}?stb_id=${serviceAccountId}`);
                if (isStale()) return;
                if (editRes?.status === true) {
                    if (Array.isArray(editRes?.data?.items) && editRes.data.items.length > 0) {
                        candidateProducts = editRes.data.items;
                    } else if (Array.isArray(editRes?.data?.products) && editRes.data.products.length > 0) {
                        candidateProducts = editRes.data.products;
                    }
                }
            }

            // 5. Fallback: fetch customer products if available
            if (candidateProducts.length === 0 && custId) {
                const custProdUrl = authState?.userRole === 1 ? getCustomersProduct : getCustomersProductRole;
                const custRes = await getClientUrlDataToken(authState?.apiUrl, `${custProdUrl}?customer_id=${custId}`);
                if (isStale()) return;
                if (custRes?.status === true && Array.isArray(custRes?.data) && custRes.data.length > 0) {
                    candidateProducts = custRes.data;
                }
            }

            // 5. Fallback: create primary hardware product entry from Service Account device info
            if (candidateProducts.length === 0) {
                const devName = saObject?.product_name || saObject?.service_account || saObject?.stb_no || `Service Device #${serviceAccountId}`;
                candidateProducts = [{
                    id: saObject?.product_id || serviceAccountId,
                    product_id: saObject?.product_id || serviceAccountId,
                    product_name: devName,
                    product_sku: saObject?.sku || saObject?.product_sku || saObject?.device_make || 'N/A',
                    product_details: saObject?.connection_type || 'Main Hardware Unit',
                    serial_no: saObject?.machine_no || saObject?.serial_no || '',
                    machine_no: saObject?.machine_no || saObject?.serial_no || ''
                }];
            }

            // Extract all stamped product records associated with this stamping / edit data
            const knownStampedRecords = [];
            const seenStampedKeys = new Set();
            const normalizeSerial = (value) => String(value || '').trim().toLowerCase();
            // The same product can appear several times (one per machine), so a stamping record
            // only belongs to a product unit when the machine numbers agree (if both have one)
            const sameMachine = (a, b) => !normalizeSerial(a) || !normalizeSerial(b) || normalizeSerial(a) === normalizeSerial(b);
            const addStampedRecord = (st) => {
                if (!st || typeof st !== 'object') return;
                const pId = st.product_id || st.id;
                const pName = st.product_name || st.name;
                const baseKey = pId ? String(pId) : (pName ? String(pName).trim().toLowerCase() : null);
                const key = baseKey ? `${baseKey}::${normalizeSerial(st.machine_no || st.serial_no)}` : null;
                if (key && !seenStampedKeys.has(key)) {
                    seenStampedKeys.add(key);
                    knownStampedRecords.push({
                        product_id: pId,
                        product_name: pName,
                        product_sku: st.product_sku || st.sku || 'N/A',
                        product_details: st.product_details || st.description || '',
                        machine_no: st.machine_no || st.serial_no || '',
                        stamping_type: st.stamping_type || st.account_type,
                        quarter: st.quarter || st.quater,
                        stamping_date: st.stamping_date || st.date || st.stamping_dt,
                        status: st.status,
                        stamping_no: st.stamping_no || st.cert_no || '',
                        remarks: st.remarks || ''
                    });
                }
            };

            if (editData) {
                const arraysToCheck = [
                    editData.products,
                    editData.items,
                    editData.raw?.products,
                    editData.raw?.items,
                    editData.raw?.stamping_details,
                    editData.stamping_details
                ];
                arraysToCheck.forEach(arr => {
                    if (Array.isArray(arr)) {
                        arr.forEach(addStampedRecord);
                    }
                });
                if (editData.current_product) {
                    addStampedRecord(editData.current_product);
                }
                if (editData.product_id || editData.product_name) {
                    addStampedRecord({
                        product_id: editData.product_id,
                        product_name: editData.product_name,
                        machine_no: editData.machine_no,
                        stamping_type: editData.stamping_type || editData.account_type,
                        quarter: editData.quarter || editData.quater,
                        stamping_date: editData.created_dt || editData.date || editData.stamping_dt,
                        status: editData.status,
                        stamping_no: editData.stamping_no,
                        remarks: editData.remarks
                    });
                }
            }

            // Ensure every product from knownStampedRecords is included in candidateProducts
            if (knownStampedRecords.length > 0) {
                knownStampedRecords.forEach((st) => {
                    const sId = st.product_id;
                    const sName = st.product_name;
                    const exists = candidateProducts.some(cp => (
                        (sId && String(cp.product_id || cp.id) === String(sId)) ||
                        (sName && cp.product_name && String(cp.product_name).trim().toLowerCase() === String(sName).trim().toLowerCase())
                    ) && sameMachine(cp.serial_no || cp.machine_no, st.machine_no));
                    if (!exists && (sId || sName)) {
                        candidateProducts.push({
                            id: sId,
                            product_id: sId,
                            product_name: sName || `Product #${sId}`,
                            product_sku: st.product_sku || 'N/A',
                            product_details: st.product_details || '',
                            serial_no: st.machine_no || '',
                            machine_no: st.machine_no || '',
                            stamping_type: st.stamping_type,
                            quarter: st.quarter,
                            stamping_date: st.stamping_date,
                            status: st.status,
                            stamping_no: st.stamping_no
                        });
                    }
                });
            }

            // Normalize products (every unit is listed, even repeated product / machine numbers) and
            // capture the originally stored stamping values so status (Stamped / Needs Update) can be derived.
            const formatted = [];
            candidateProducts.forEach((item, idx) => {
                const pId = item.product_id || item.id || (idx + 1);
                const pName = item.product_name || item.name || item.service_plan_name || `Product #${idx + 1}`;
                const pSku = item.product_sku || item.sku || item.product_code || 'N/A';
                const pDetails = item.warranty_name
                    ? `Warranty: ${item.warranty_name}`
                    : (item.product_details || item.description || item.service_plan_name || '');
                const pImage = item.product_image || item.image || '';

                // Serial number determination
                const pSerial = item.serial_no || item.machine_no || item.machine_no || saObject?.machine_no || '';

                // Match this product unit against known stamping records (same product and machine)
                const matchedStamped = knownStampedRecords.find(st => (
                    (st.product_id && String(st.product_id) === String(pId)) ||
                    (st.product_name && pName && String(st.product_name).trim().toLowerCase() === String(pName).trim().toLowerCase())
                ) && sameMachine(st.machine_no, pSerial));

                const editMachineMatches = sameMachine(editData?.machine_no || editData?.raw?.machine_no, pSerial);
                const isThisProductInEdit = Boolean(
                    editData?.id && (
                        matchedStamped ||
                        item.stamping_date ||
                        item.date ||
                        (editData.product_id && String(editData.product_id) === String(pId) && editMachineMatches) ||
                        (editData.raw?.product_id && String(editData.raw.product_id) === String(pId) && editMachineMatches) ||
                        (editData.product_name && String(editData.product_name).trim().toLowerCase() === String(pName).trim().toLowerCase() && editMachineMatches) ||
                        (!editData.product_id && !editData.raw?.product_id && !editData.product_name && knownStampedRecords.length === 0 && candidateProducts.length === 1)
                    )
                );

                // Account type determination (LMS, GATC) - use matched stamping record first
                const rawType = matchedStamped?.stamping_type ||
                    matchedStamped?.account_type ||
                    item.stamping_type ||
                    item.account_type ||
                    (isThisProductInEdit && knownStampedRecords.length <= 1 ? (editData.stamping_type || editData.account_type || editData.raw?.stamping_type) : null);
                const matchedType = rawType
                    ? (ACCOUNT_TYPE_OPTIONS.find(t =>
                        t.value.toLowerCase() === String(rawType).toLowerCase() ||
                        t.label.toLowerCase() === String(rawType).toLowerCase()
                    ) || null)
                    : null;

                // Quarter determination - use matched stamping record first
                const rawQ = matchedStamped?.quarter ||
                    matchedStamped?.quater ||
                    item.quater ||
                    item.quarter ||
                    (isThisProductInEdit && knownStampedRecords.length <= 1 ? (editData.quater || editData.quarter || editData.raw?.quater || editData.raw?.quarter) : null);
                const qCode = rawQ ? getQuarterCode(rawQ) : '';
                const matchedQ = qCode
                    ? (QUARTER_OPTIONS.find(q => q.value === qCode || q.label.toLowerCase() === String(rawQ).toLowerCase()) || null)
                    : null;

                // Stamping Date determination - use matched stamping record first
                const rawDateVal = matchedStamped?.stamping_date ||
                    matchedStamped?.date ||
                    item.stamping_date ||
                    item.date ||
                    (isThisProductInEdit && knownStampedRecords.length <= 1 ? (editData.stamping_date || editData.date || editData.raw?.stamping_date) : '');
                const pDate = normalizeDate(rawDateVal);

                // Status determination
                const rawStatus = matchedStamped?.status || item.status || (isThisProductInEdit ? editData?.status : null) || 'pending';
                const matchedStatus = STATUS_OPTIONS.find(s => s.value.toLowerCase() === String(rawStatus).toLowerCase()) || STATUS_OPTIONS[0];

                const pStampingNo = matchedStamped?.stamping_no || item.stamping_no || (isThisProductInEdit ? editData?.stamping_no : '') || '';

                // A product counts as already stamped when a stamping date is stored for it
                const wasStamped = Boolean(pDate) && Boolean(matchedStamped || item.stamping_date || item.date || (isThisProductInEdit && editData?.id));

                formatted.push({
                    uniqueKey: `prod_${pId}_${idx}`,
                    product_id: pId,
                    product_name: pName,
                    product_sku: pSku,
                    product_details: pDetails,
                    product_image: pImage,
                    machine_no: pSerial,
                    account_type: matchedType,
                    quarter: matchedQ,
                    stamping_date: pDate,
                    stamping_no: pStampingNo,
                    status: matchedStatus,
                    remarks: matchedStamped?.remarks || item.remarks || '',
                    original: wasStamped
                        ? { account_type: matchedType?.value || '', quarter: matchedQ?.value || '', stamping_date: pDate }
                        : null,
                    // Set when the user edits this product individually; bulk apply will not overwrite it by default
                    is_customized: false,
                    // By default in add mode, products are unselected.
                    // In edit mode, select all products associated with stamping records.
                    is_selected: Boolean(editData?.id ? isThisProductInEdit : false)
                });
            });

            // In edit mode, display all products associated with stamping records
            const finalProducts = (editData?.id && formatted.some(p => p.is_selected))
                ? formatted.filter(p => p.is_selected)
                : formatted;

            setProductsList(finalProducts);
        } catch (err) {
            if (isStale()) return;
            console.error("Error fetching products for service account:", err);
            setProductsList([]);
        } finally {
            if (!isStale()) setLoadingProducts(false);
        }
    }, [authState?.apiUrl, authState?.userRole, defaultAccountType, defaultQuarter, defaultStampingDate, editData, selectedCustomer?.value]);

    const fetchServiceAccountsForCustomer = async (customerId, preselectAccount = null) => {
        if (!customerId) return;
        const requestId = ++stbRequestRef.current;
        const isStale = () => requestId !== stbRequestRef.current;
        setLoadingStbs(true);
        try {
            const stbUrl = authState?.userRole === 1 ? selectCustStb : selectCustStbRole;
            const res = await getClientUrlDataToken(
                authState?.apiUrl,
                `${stbUrl}?customer_id=${customerId}`
            );
            if (isStale()) return;

            let stbList = [];
            if (res?.status === true && res?.data?.stb && Array.isArray(res.data.stb)) {
                stbList = res.data.stb;
            } else if (res?.data && Array.isArray(res.data)) {
                stbList = res.data;
            }

            const formattedStbs = stbList.map(item => {
                const saName = item.service_account || item.stb_no || item.serial_no || `SA-${item.id}`;
                return {
                    value: item.id || saName,
                    id: item.id,
                    label: item.service_account ? (item.stb_no ? `${item.service_account} )` : item.service_account) : (item.stb_no || `SA #${item.id}`),
                    stb_no: item.stb_no || saName,
                    service_account: item.service_account || saName,
                    quater: item.quater || item.quarter || item.stamping_quarter || '',
                    quarter: item.quarter || item.quater || item.stamping_quarter || '',
                    machine_no: item.machine_no || item.serial_no || '',
                    stamping_type: item.stamping_type || item.account_type || '',
                    route_id: item.route_id || item.route || null,
                    route_name: item.route_name || item.route || '',
                    taluk_id: item.taluk_id || item.taluk || null,
                    taluk_name: item.taluk_name || item.taluk || '',
                    ...item
                };
            });

            setStbOptions(formattedStbs);

            if (preselectAccount && preselectAccount !== 'N/A') {
                const matchedSA = formattedStbs.find(s =>
                    s.id === preselectAccount ||
                    s.value === preselectAccount ||
                    s.stb_no === preselectAccount ||
                    s.service_account === preselectAccount ||
                    String(s.id) === String(preselectAccount) ||
                    String(s.value) === String(preselectAccount) ||
                    String(s.stb_no) === String(preselectAccount) ||
                    String(s.service_account) === String(preselectAccount)
                );

                if (matchedSA) {
                    setSelectedServiceAccount(matchedSA);

                    // Auto-fill route and taluk if available
                    if (!selectedRoute && (matchedSA.route_id || matchedSA.route || matchedSA.route_name)) {
                        const saRoute = matchedSA.route_id || matchedSA.route || matchedSA.route_name;
                        const matchedRoute = routeOptions.find(r =>
                            String(r.value) === String(saRoute) ||
                            String(r.id) === String(saRoute) ||
                            String(r.route_id) === String(saRoute) ||
                            r.label?.toLowerCase() === String(saRoute)?.toLowerCase() ||
                            r.name?.toLowerCase() === String(saRoute)?.toLowerCase()
                        );
                        if (matchedRoute) setSelectedRoute(matchedRoute);
                    }
                    if (!selectedTaluk && (matchedSA.taluk_id || matchedSA.taluk || matchedSA.taluk_name)) {
                        const saTaluk = matchedSA.taluk_id || matchedSA.taluk || matchedSA.taluk_name;
                        const matchedTaluk = talukOptions.find(t =>
                            String(t.value) === String(saTaluk) ||
                            String(t.id) === String(saTaluk) ||
                            String(t.taluk_id) === String(saTaluk) ||
                            t.label?.toLowerCase() === String(saTaluk)?.toLowerCase() ||
                            t.name?.toLowerCase() === String(saTaluk)?.toLowerCase()
                        );
                        if (matchedTaluk) setSelectedTaluk(matchedTaluk);
                    }

                    // Automatically fetch products for the preselected Service Account
                    fetchProductsForServiceAccount(matchedSA.id || matchedSA.value, customerId, matchedSA);
                } else {
                    const fallbackLabel = (editData?.service_account && editData?.service_account !== 'N/A')
                        ? editData.service_account
                        : ((editData?.stb_no && editData?.stb_no !== 'N/A') ? editData.stb_no : `SA #${preselectAccount}`);
                    const fallbackSA = {
                        value: preselectAccount,
                        id: preselectAccount,
                        label: fallbackLabel,
                        service_account: fallbackLabel
                    };
                    setSelectedServiceAccount(fallbackSA);
                    fetchProductsForServiceAccount(preselectAccount, customerId, fallbackSA);
                }
            }
        } catch (err) {
            if (isStale()) return;
            console.error("Error fetching service accounts for customer:", err);
            setStbOptions([]);
            setSelectedServiceAccount(null);
            setProductsList([]);
        } finally {
            if (!isStale()) setLoadingStbs(false);
        }
    };

    const handleCustomerChange = (selected) => {
        // Discard any in-flight account/product responses for the previous customer
        stbRequestRef.current++;
        productsRequestRef.current++;
        setLoadingStbs(false);
        setLoadingProducts(false);
        setSelectedCustomer(selected);
        setSelectedServiceAccount(null);
        setProductsList([]);
        setProductErrors({});

        if (!editData) {
            setSelectedRoute(null);
            setSelectedTaluk(null);
        }
        if (selected) {
            fetchServiceAccountsForCustomer(selected.value);
        } else {
            setStbOptions([]);
        }
        if (errors.customer) {
            setErrors(prev => ({ ...prev, customer: null }));
        }
    };

    const handleServiceAccountChange = (sel) => {
        setSelectedServiceAccount(sel);
        if (errors.service_account) setErrors(prev => ({ ...prev, service_account: null }));

        if (!sel) {
            productsRequestRef.current++;
            setLoadingProducts(false);
            setProductsList([]);
            return;
        }


        // Auto-fill Route if available
        const saRoute = sel?.route_id || sel?.route || sel?.route_name;
        if (saRoute) {
            const matchedRoute = routeOptions.find(r =>
                String(r.value) === String(saRoute) ||
                String(r.id) === String(saRoute) ||
                String(r.route_id) === String(saRoute) ||
                r.label?.toLowerCase() === String(saRoute)?.toLowerCase() ||
                r.name?.toLowerCase() === String(saRoute)?.toLowerCase()
            );
            if (matchedRoute) setSelectedRoute(matchedRoute);
        }

        // Auto-fill Taluk if available
        const saTaluk = sel?.taluk_id || sel?.taluk || sel?.taluk_name;
        if (saTaluk) {
            const matchedTaluk = talukOptions.find(t =>
                String(t.value) === String(saTaluk) ||
                String(t.id) === String(saTaluk) ||
                String(t.taluk_id) === String(saTaluk) ||
                t.label?.toLowerCase() === String(saTaluk)?.toLowerCase() ||
                t.name?.toLowerCase() === String(saTaluk)?.toLowerCase()
            );
            if (matchedTaluk) setSelectedTaluk(matchedTaluk);
        }

        // Automatically fetch and display associated products for this Service Account
        fetchProductsForServiceAccount(sel.id || sel.value, selectedCustomer?.value, sel);
    };

    // Leaving the review step / clearing stale save errors whenever the service account changes
    useEffect(() => {
        setIsReviewing(false);
        setSaveError('');
        setProductSearch('');
        setExpandedKeys({});
    }, [selectedServiceAccount]);

    const clearProductError = (key, field = null) => {
        setProductErrors(prev => {
            if (!prev[key]) return prev;
            const next = { ...prev };
            if (!field) {
                delete next[key];
                return next;
            }
            const fieldErrors = { ...prev[key] };
            delete fieldErrors[field];
            if (Object.keys(fieldErrors).length === 0) delete next[key];
            else next[key] = fieldErrors;
            return next;
        });
    };

    /**
     * Update an individual product's stamping attribute (identified by its uniqueKey so
     * filtering/searching never maps a change to the wrong product).
     */
    const handleProductFieldChange = (key, field, value) => {
        setProductsList(prev => prev.map(p => {
            if (p.uniqueKey !== key) return p;
            const next = { ...p, [field]: value };
            // A date left over from a different quarter can no longer be picked, so clear it
            if (field === 'quarter' && value && !isDateInQuarter(p.stamping_date, value)) {
                next.stamping_date = '';
            }
            if (['account_type', 'quarter', 'stamping_date'].includes(field)) {
                next.is_customized = true;
            }
            return next;
        }));
        setIsReviewing(false);
        setSaveError('');
        clearProductError(key, field);
        // Quarter and date are validated together
        if (field === 'quarter') clearProductError(key, 'stamping_date');
    };

    const toggleProductSelection = (key, checked) => {
        setProductsList(prev => prev.map(p => (p.uniqueKey === key ? { ...p, is_selected: checked } : p)));
        if (!checked) {
            clearProductError(key);
            setExpandedKeys(prev => ({ ...prev, [key]: false }));
        } else {
            // Automatically expand editor when product is selected if stamping details are incomplete
            const prod = productsList.find(p => p.uniqueKey === key);
            if (prod && (!prod.stamping_date || !prod.account_type || !prod.quarter)) {
                setExpandedKeys(prev => ({ ...prev, [key]: true }));
            }
        }
        setIsReviewing(false);
        setSaveError('');
    };

    const toggleProductEditor = (key) => {
        setExpandedKeys(prev => ({ ...prev, [key]: !prev[key] }));
    };

    // Derived product data
    const productsWithStatus = useMemo(
        () => productsList.map(p => ({ ...p, stampStatus: getProductStatus(p) })),
        [productsList]
    );

    const filteredProducts = useMemo(() => {
        const q = productSearch.trim().toLowerCase();
        if (!q) return productsWithStatus;
        return productsWithStatus.filter(p =>
            [p.product_name, p.machine_no, p.product_details, p.product_sku]
                .some(v => v && String(v).toLowerCase().includes(q))
        );
    }, [productsWithStatus, productSearch]);

    const selectedProducts = productsWithStatus.filter(p => p.is_selected);
    const selectedProductsCount = selectedProducts.length;
    const stampedCount = productsWithStatus.filter(p => p.stampStatus === 'stamped').length;
    const needsUpdateCount = productsWithStatus.filter(p => p.stampStatus === 'needs_update').length;
    const pendingCount = productsWithStatus.filter(p => p.stampStatus === 'pending').length;
    const selectedIncompleteCount = selectedProducts.filter(p => !isProductComplete(p)).length;

    const visibleSelectedCount = filteredProducts.filter(p => p.is_selected).length;
    const allVisibleSelected = filteredProducts.length > 0 && visibleSelectedCount === filteredProducts.length;
    const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

    /**
     * Select / deselect every product currently visible (respects the search filter).
     */
    const handleToggleAllVisible = (checked) => {
        const visibleKeys = new Set(filteredProducts.map(p => p.uniqueKey));
        setProductsList(prev => prev.map(p => (visibleKeys.has(p.uniqueKey) ? { ...p, is_selected: checked } : p)));
        if (!checked) {
            setProductErrors(prev => {
                const next = { ...prev };
                visibleKeys.forEach(k => delete next[k]);
                return next;
            });
        }
        setIsReviewing(false);
        setSaveError('');
    };

    /**
     * Validates form and all product-level stamping requirements.
     */
    const validateForm = () => {
        const newErrors = {};

        if (!selectedCustomer) {
            newErrors.customer = "Please select a customer";
        }
        if (!selectedServiceAccount) {
            newErrors.service_account = "Please select a service account";
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            const firstKey = Object.keys(newErrors)[0];
            toast.warning(newErrors[firstKey]);
            const el = document.getElementById(`field-${firstKey}`);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return false;
        }

        if (productsList.length === 0) {
            toast.warning("No products found for the selected Service Account.");
            return false;
        }

        if (selectedProductsCount === 0) {
            toast.warning("Please select at least one product to stamp.");
            return false;
        }

        const newProdErrors = {};
        let firstInvalidKey = null;

        productsList.forEach((prod) => {
            if (!prod.is_selected) return;

            const pErr = {};
            if (!prod.account_type) pErr.account_type = "Choose an account type";
            if (!prod.quarter) pErr.quarter = "Choose a quarter";
            if (!prod.stamping_date || !prod.stamping_date.trim()) {
                pErr.stamping_date = "Pick a stamping date";
            } else if (prod.quarter && !isDateInQuarter(prod.stamping_date, prod.quarter)) {
                pErr.stamping_date = `Date must fall in ${prod.quarter.label} (${getQuarterDateBounds(prod.quarter).label})`;
            }

            if (Object.keys(pErr).length > 0) {
                newProdErrors[prod.uniqueKey] = pErr;
                if (!firstInvalidKey) firstInvalidKey = prod.uniqueKey;
            }
        });

        if (firstInvalidKey) {
            setProductErrors(newProdErrors);
            // Open the editors of the products with problems so the messages are visible
            setExpandedKeys(prev => {
                const next = { ...prev };
                Object.keys(newProdErrors).forEach(k => { next[k] = true; });
                return next;
            });
            // Make sure the invalid product is not hidden by the search filter
            setProductSearch('');
            const count = Object.keys(newProdErrors).length;
            toast.warning(`${count} selected ${count === 1 ? 'product needs' : 'products need'} stamping details. Check the highlighted ${count === 1 ? 'product' : 'products'}.`);
            setTimeout(() => {
                const el = document.getElementById(`product-card-${firstInvalidKey}`);
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
            return false;
        }

        setErrors({});
        setProductErrors({});
        return true;
    };

    /**
     * Persists product-level stamping records. Selected products and entered values are
     * kept intact on failure so the user can simply retry.
     */
    const saveStamping = async () => {
        if (submitLockRef.current) return; // prevent duplicate submissions
        submitLockRef.current = true;
        setSubmitting(true);
        setSaveError('');

        // Prepare product-level stamping items for selected products only
        const items = productsList
            .filter(p => p.is_selected)
            .map(p => {
                const qVal = p.quarter?.value || getQuarterCode(p.quarter?.label || p.quarter);
                const accType = p.account_type?.value || (typeof p.account_type === 'string' ? p.account_type : p.account_type?.label || '');

                return {
                    quarter: qVal,
                    stamping_date: p.stamping_date || '',
                    product_id: Number(p.product_id || p.id),
                    machine_no: p.machine_no || '',
                    stamping_type: accType
                };
            });

        const apiPayload = {
            customer_id: Number(selectedCustomer?.value),
            service_account_id: Number(selectedServiceAccount?.id || selectedServiceAccount?.value),
            items: items
        };

        let succeeded = false;

       
        try {
            const clientUrl = authState?.apiUrl || importURL || "https://watercrm.quadleo.com/public";
            const cleanClientUrl = clientUrl.endsWith('/') ? clientUrl.slice(0, -1) : clientUrl;
            const baseEndpoint = authState?.userRole === 1 ? "/admin/stampings" : "/stampings";
            const endpoint = editData?.id ? `${baseEndpoint}/${editData.id}` : baseEndpoint;

            const res = editData?.id
                ? await putClientUrlWithToken(cleanClientUrl, endpoint, apiPayload)
                : await postClientUrlWithToken(cleanClientUrl, endpoint, apiPayload);

            if ((res?.status === 200 || res?.status === 201 || res?.data?.status === true) && res?.data?.status === true) {
                succeeded = true;
                if (onSave) {
                    onSave(res?.data?.data || apiPayload);
                }
                const count = items.length;
                toast.success(res?.data?.message || (editData?.id
                    ? "Stamping record updated successfully!"
                    : `Stamping saved for ${count} ${count === 1 ? 'product' : 'products'}.`));
                editData?.dueReport ? navigate('/stamping/stamping-due') : navigate('/stamping/stamping-customer');
            } else {
                const msg = res?.data?.message || "The server did not accept the stamping details.";
                setSaveError(`${msg} Your selections are kept - please review and try again.`);
                toast.error(msg);
            }
        } catch (err) {
            console.error("Save stamping error:", err);
            const msg = err?.response?.data?.message || "Could not reach the server.";
            setSaveError(`${msg} Your selections are kept - please check your connection and try again.`);
            toast.error("Failed to save stamping records.");
        } finally {
            if (!succeeded) {
                submitLockRef.current = false;
                setSubmitting(false);
            }
        }
    };

    /**
     * Primary action: validate, show a confirmation summary when several products are
     * involved, then save.
     */
    const handleSubmit = (e) => {
        e.preventDefault();
        if (submitting || submitLockRef.current) return;
        if (!validateForm()) return;

        if (selectedProductsCount > 1 && !isReviewing) {
            setIsReviewing(true);
            setTimeout(() => {
                const el = document.getElementById('stamping-review');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 50);
            return;
        }
        saveStamping();
    };

    // Workflow step indicator
    const currentStep = !selectedServiceAccount
        ? 1
        : selectedProductsCount === 0
            ? 2
            : selectedIncompleteCount > 0
                ? 3
                : 4;
    const WORKFLOW_STEPS = ['Choose service account', 'Select products', 'Add stamping details', 'Review & save'];

    const primaryDisabled = submitting || loadingProducts || !selectedServiceAccount || productsList.length === 0 || selectedProductsCount === 0;
    const primaryLabel = submitting
        ? 'Saving...'
        : isReviewing
            ? `Confirm & Save ${selectedProductsCount} Products`
            : selectedProductsCount > 1
                ? `Review & Save (${selectedProductsCount})`
                : (editData?.id ? 'Update Stamping' : 'Save Stamping');

    const sectionTitle = (icon, text) => (
        <div className="d-flex align-items-center gap-2 text-primary">
            {icon}
            <h6 className="mb-0 fw-bold text-uppercase" style={{ fontSize: "0.8rem", letterSpacing: "0.5px" }}>
                {text}
            </h6>
        </div>
    );

    const renderStatusBadge = (statusKey) => {
        const meta = PRODUCT_STATUS_META[statusKey] || PRODUCT_STATUS_META.pending;
        return (
            <span
                className="d-inline-flex align-items-center gap-1 fw-semibold rounded-pill px-2 py-1"
                style={{ fontSize: "0.72rem", color: meta.color, backgroundColor: meta.bg, border: `1px solid ${meta.border}`, whiteSpace: 'nowrap' }}
                title={meta.hint}
            >
                {statusKey === 'stamped' ? <FiCheckCircle size={11} /> : statusKey === 'needs_update' ? <FiAlertCircle size={11} /> : null}
                {meta.label}
            </span>
        );
    };

    const renderStatChip = (label, value, color) => (
        <span
            className="d-inline-flex align-items-center gap-1 rounded-pill px-2 py-1 border bg-white"
            style={{ fontSize: "0.75rem", color: '#475569' }}
        >
            <span className="fw-bold" style={{ color }}>{value}</span> {label}
        </span>
    );

    return (
        <>
            {submitting && <Transparentspinner />}
            <Breadcrumbs
                mainTitle={'Add Stamping'}
                parent="Service Accounts"
                title={editData?.id ? 'Edit Stamping' : 'Add Stamping'}
            />
            <Card className="shadow-sm border-0">
                <CardHeader className="bg-white border-bottom py-3 px-4">
                    <div className="d-flex align-items-center gap-3">
                        <div
                            className="rounded-3 d-flex align-items-center justify-content-center text-white flex-shrink-0"
                            style={{
                                width: "42px",
                                height: "42px",
                                background: "linear-gradient(135deg, #0061cd 0%, #206bc4 100%)",
                                boxShadow: "0 4px 12px rgba(0, 97, 205, 0.25)"
                            }}
                        >
                            <FaStamp size={20} />
                        </div>
                        <div>
                            <h5 className="mb-0 fw-bold text-dark">
                                {editData?.id ? "Edit Stamping Account" : "Add Stamping Account"}
                            </h5>
                            <small className="text-muted">
                                Pick a service account, tick the products to stamp, enter the details once and save.
                            </small>
                        </div>
                    </div>
                </CardHeader>

                <Form onSubmit={handleSubmit} noValidate>
                    <CardBody className="p-3 p-md-4" style={{ backgroundColor: "#f8fafc" }}>
                        {/* Workflow progress */}
                        <div className="d-flex flex-wrap gap-2 mb-3" aria-label="Stamping progress">
                            {WORKFLOW_STEPS.map((label, i) => {
                                const stepNo = i + 1;
                                const done = stepNo < currentStep;
                                const active = stepNo === currentStep;
                                return (
                                    <div
                                        key={label}
                                        className="d-flex align-items-center gap-2 rounded-pill px-3 py-1 border"
                                        style={{
                                            fontSize: "0.78rem",
                                            backgroundColor: active ? '#eef4ff' : done ? '#e6f7f1' : '#ffffff',
                                            borderColor: active ? '#0061cd' : done ? '#0ca678' : '#e2e8f0',
                                            color: active ? '#0061cd' : done ? '#0b7a5a' : '#94a3b8',
                                            fontWeight: active ? 600 : 500
                                        }}
                                        aria-current={active ? 'step' : undefined}
                                    >
                                        <span
                                            className="d-inline-flex align-items-center justify-content-center rounded-circle"
                                            style={{
                                                width: 18, height: 18, fontSize: "0.7rem",
                                                backgroundColor: active ? '#0061cd' : done ? '#0ca678' : '#e2e8f0',
                                                color: active || done ? '#fff' : '#64748b'
                                            }}
                                        >
                                            {done ? <FiCheck size={11} /> : stepNo}
                                        </span>
                                        {label}
                                    </div>
                                );
                            })}
                        </div>

                        {/* SECTION 1: Customer & Service Account Details */}
                        <div className="bg-white rounded-3 p-3 border mb-3 shadow-sm">
                            <div className="mb-3 pb-2 border-bottom">
                                {sectionTitle(<FiTag size={16} />, '1. Customer & Service Account')}
                            </div>

                            <Row className="g-3">
                                {/* Stamping Customer Dropdown */}
                                <Col md="6">
                                    <FormGroup className="mb-0" id="field-customer" data-field="customer">
                                        <Label htmlFor="customer-input" className="form-label fw-semibold text-dark">
                                            Stamping Customer <span className="text-danger">*</span>
                                        </Label>
                                        <Typeahead
                                            id="customer"
                                            inputId="customer-input"
                                            name="customer"
                                            options={customerOptions}
                                            value={selectedCustomer}
                                            onChange={handleCustomerChange}
                                            placeholder={loadingCustomers ? "Loading customers..." : "Search or select a customer..."}
                                            isClearable={!isCustomerLocked}
                                            disabled={isCustomerLocked || loadingCustomers}
                                        />
                                        {errors.customer && (
                                            <span className="text-danger small mt-1 d-block">{errors.customer}</span>
                                        )}
                                    </FormGroup>
                                </Col>

                                {/* Service Account Dropdown */}
                                <Col md="6">
                                    <FormGroup className="mb-0" id="field-service_account" data-field="service_account">
                                        <Label htmlFor="service_account-input" className="form-label fw-semibold text-dark">
                                            Service Account <span className="text-danger">*</span>
                                        </Label>
                                        <Typeahead
                                            id="service_account"
                                            inputId="service_account-input"
                                            name="service_account"
                                            options={stbOptions}
                                            value={selectedServiceAccount}
                                            onChange={handleServiceAccountChange}
                                            placeholder={
                                                !selectedCustomer
                                                    ? "Select a customer first"
                                                    : loadingStbs
                                                        ? "Loading service accounts..."
                                                        : stbOptions.length === 0
                                                            ? "No service accounts found"
                                                            : "Search or select a service account..."
                                            }
                                            disabled={isServiceAccountLocked || !selectedCustomer || loadingStbs}
                                            isLoading={loadingStbs}
                                            isClearable={!isServiceAccountLocked}
                                        />
                                        {errors.service_account ? (
                                            <span className="text-danger small mt-1 d-block">{errors.service_account}</span>
                                        ) : (
                                            <small className="text-muted d-block mt-1">Its products load automatically below.</small>
                                        )}
                                    </FormGroup>
                                </Col>
                            </Row>
                        </div>

                        {/* SECTION 2: Products & Stamping */}
                        <div className="bg-white rounded-3 p-3 border mb-3 shadow-sm">
                            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3 pb-2 border-bottom">
                                {sectionTitle(<FaBox size={16} />, '2. Products & Stamping')}
                                {productsList.length > 0 && !loadingProducts && (
                                    <div className="d-flex flex-wrap gap-1">
                                        {renderStatChip('Total', productsList.length, '#0f172a')}
                                        {renderStatChip('Selected', selectedProductsCount, '#0061cd')}
                                        {renderStatChip('Stamped', stampedCount, '#0b7a5a')}
                                        {renderStatChip('Pending', pendingCount, '#64748b')}
                                        {needsUpdateCount > 0 && renderStatChip('Needs Update', needsUpdateCount, '#b45309')}
                                    </div>
                                )}
                            </div>

                            {/* State: No Service Account selected */}
                            {!selectedServiceAccount && (
                                <div className="text-center py-5 bg-light rounded-3 border border-dashed">
                                    <FaBox size={36} className="text-muted mb-2 opacity-50" />
                                    <h6 className="fw-bold text-dark mb-1">Select a service account to begin</h6>
                                    <p className="text-muted small mb-0">
                                        Choose a customer and service account above. Their products will appear here.
                                    </p>
                                </div>
                            )}

                            {/* State: Loading products */}
                            {selectedServiceAccount && loadingProducts && (
                                <div className="text-center py-5 bg-light rounded-3 border">
                                    <Spinner color="primary" size="md" />
                                    <h6 className="fw-bold text-dark mt-3 mb-1">Loading products...</h6>
                                    <p className="text-muted small mb-0">
                                        Fetching the products linked to this service account.
                                    </p>
                                </div>
                            )}

                            {/* State: No products returned */}
                            {selectedServiceAccount && !loadingProducts && productsList.length === 0 && (
                                <div className="text-center py-4 bg-light rounded-3 border">
                                    <FaBox size={32} className="text-muted mb-2 opacity-50" />
                                    <h6 className="fw-bold text-dark mb-1">No products on this service account</h6>
                                    <p className="text-muted small mb-0">
                                        Add products to the service account first, or choose a different service account.
                                    </p>
                                </div>
                            )}

                            {selectedServiceAccount && !loadingProducts && productsList.length > 0 && (
                                <>
                                    {/* Search + select all */}
                                    <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                        <div className="form-check mb-0 d-flex align-items-center gap-2">
                                            <input
                                                id="select-all-products"
                                                type="checkbox"
                                                className="form-check-input m-0"
                                                style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                checked={allVisibleSelected}
                                                ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                                                onChange={(e) => handleToggleAllVisible(e.target.checked)}
                                                disabled={filteredProducts.length === 0}
                                            />
                                            <label htmlFor="select-all-products" className="form-check-label fw-semibold small text-dark mb-0" style={{ cursor: 'pointer' }}>
                                                {allVisibleSelected ? 'Deselect all' : 'Select all'}
                                                {productSearch.trim() ? ` matching (${filteredProducts.length})` : ` (${productsList.length})`}
                                            </label>
                                        </div>

                                    </div>

                                    {selectedProductsCount === 0 && (
                                        <div className="small text-muted mb-2 d-flex align-items-center gap-1">
                                            <FiInfo size={12} />
                                            Products are unselected by default. Tick the product(s) you want to stamp, or use "Select all".
                                        </div>
                                    )}

                                    {/* Product list */}
                                    {filteredProducts.length === 0 ? (
                                        <div className="text-center py-4 bg-light rounded-3 border">
                                            <FiSearch size={26} className="text-muted mb-2 opacity-50" />
                                            <h6 className="fw-bold text-dark mb-1">No products match "{productSearch}"</h6>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0"
                                                onClick={() => setProductSearch('')}
                                            >
                                                Clear search
                                            </button>
                                        </div>
                                    ) : (
                                        <Row className="g-3 align-items-start">
                                            {filteredProducts.map((product) => {
                                                const key = product.uniqueKey;
                                                const isSelected = Boolean(product.is_selected);
                                                const pErr = productErrors[key];
                                                const hasError = Boolean(pErr);
                                                const isExpanded = isSelected && Boolean(expandedKeys[key]);
                                                const statusMeta = PRODUCT_STATUS_META[product.stampStatus];
                                                const dateOutOfQuarter = product.stamping_date && product.quarter && !isDateInQuarter(product.stamping_date, product.quarter);
                                                const accent = hasError ? '#dc3545' : isSelected ? '#0061cd' : statusMeta.border;

                                                return (
                                                    <Col lg="6" xs="12" key={key} className="align-self-start">
                                                        <div
                                                            id={`product-card-${key}`}
                                                            className="rounded-3 border"
                                                            style={{
                                                                backgroundColor: hasError ? '#fff8f8' : isSelected ? '#ffffff' : '#f8fafc',
                                                                borderColor: hasError ? '#f1aeb5' : isSelected ? '#b8d5ff' : '#e2e8f0',
                                                                borderLeft: `4px solid ${accent}`,
                                                                boxShadow: isSelected ? '0 2px 8px rgba(0,0,0,0.04)' : 'none',
                                                                transition: 'all .15s ease'
                                                            }}
                                                        >
                                                            <div className="d-flex align-items-center flex-wrap gap-2 gap-md-3 p-2 px-3">

                                                                <div>
                                                                    <input
                                                                        type="checkbox"
                                                                        className="form-check-input m-0 flex-shrink-0"
                                                                        style={{ width: 18, height: 18, cursor: 'pointer' }}
                                                                        id={`product-check-${key}`}
                                                                        checked={isSelected}
                                                                        onChange={(e) => toggleProductSelection(key, e.target.checked)}
                                                                        aria-label={`Select ${product.product_name}`}
                                                                    />

                                                                    {/* Product info */}
                                                                    <label
                                                                        htmlFor={`product-check-${key}`}
                                                                        className="mb-0 mx-3 flex-grow-1"
                                                                        style={{ minWidth: 160, flexBasis: 180, cursor: 'pointer' }}
                                                                    >
                                                                        <span className={`fw-bold d-block ${isSelected ? 'text-dark' : 'text-secondary'}`} style={{ fontSize: "0.92rem" }}>
                                                                            {product.product_name}
                                                                        </span>
                                                                        <span className="d-flex flex-wrap align-items-center gap-2 text-muted" style={{ fontSize: "0.76rem" }}>
                                                                            <span className="d-inline-flex align-items-center gap-1">
                                                                                <FaBarcode size={11} />
                                                                                {product.machine_no
                                                                                    ? <>S/N: <strong className="text-dark">{product.machine_no}</strong></>
                                                                                    : <em>No serial number</em>}
                                                                            </span>
                                                                            {product.product_sku && product.product_sku !== 'N/A' && <span>· SKU {product.product_sku}</span>}
                                                                        </span>
                                                                    </label>
                                                                </div>

                                                                {/* Stamping summary */}
                                                                <div className="d-flex flex-wrap align-items-center gap-1" style={{ fontSize: "0.76rem" }}>
                                                                    <span className="rounded-2 px-2 py-1 bg-light border text-dark">{product.account_type?.label || '—'}</span>
                                                                    <span className="rounded-2 px-2 py-1 bg-light border text-dark">{product.quarter?.label || '—'}</span>
                                                                    <span
                                                                        className={`rounded-2 px-2 py-1 border d-inline-flex align-items-center gap-1 ${product.stamping_date && !dateOutOfQuarter ? 'bg-light text-dark' : ''}`}
                                                                        style={!product.stamping_date || dateOutOfQuarter ? { backgroundColor: '#fff4e5', color: '#b45309', borderColor: '#f5c26b' } : undefined}
                                                                    >
                                                                        <FiCalendar size={11} />
                                                                        {product.stamping_date ? formatDisplayDate(product.stamping_date) : 'No date'}
                                                                    </span>
                                                                </div>

                                                                <div className="d-flex align-items-center gap-2 ms-auto">
                                                                    {isSelected && (
                                                                        <button
                                                                            type="button"
                                                                            className={`btn btn-sm ${isExpanded ? 'btn-primary' : 'btn-outline-primary'} d-inline-flex align-items-center gap-1`}
                                                                            style={{ fontSize: "0.75rem", padding: "3px 8px", borderRadius: "4px" }}
                                                                            onClick={() => toggleProductEditor(key)}
                                                                            title={isExpanded ? "Collapse" : "Edit stamping details"}
                                                                        >
                                                                            {isExpanded ? <FiChevronUp size={13} /> : <FiEdit2 size={13} />}
                                                                            <span>{isExpanded ? "Close" : "Edit"}</span>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {hasError && !isExpanded && (
                                                                <div className="px-3 pb-2 text-danger small">
                                                                    {Object.values(pErr).join(' · ')}
                                                                </div>
                                                            )}

                                                            {/* Individual product editor */}
                                                            {isExpanded && (
                                                                <div
                                                                    id={`product-editor-${key}`}
                                                                    className="border-top  py-3 px-3 mt-auto"
                                                                    style={{ backgroundColor: '#fbfdff' }}
                                                                >
                                                                    <Row className="g-2 g-md-3">
                                                                        {/* <Col sm="6" xs="12">
                                                                            <Label className="form-label fw-semibold text-dark small mb-1" htmlFor={`serial-${key}`}>
                                                                                Serial Number
                                                                            </Label>
                                                                            <Input
                                                                                id={`serial-${key}`}
                                                                                type="text"
                                                                                className="form-control form-control-sm"
                                                                                style={{ height: "38px", borderRadius: "6px" }}
                                                                                placeholder="Machine serial number"
                                                                                value={product.serial_number || ''}
                                                                                onChange={(e) => handleProductFieldChange(key, 'serial_number', e.target.value)}
                                                                            />
                                                                        </Col> */}
                                                                        <Col sm="6" xs="12">
                                                                            <Label className="form-label fw-semibold text-dark small mb-1" htmlFor={`type-${key}`}>
                                                                                Account Type <span className="text-danger">*</span>
                                                                            </Label>
                                                                            <Select
                                                                                inputId={`type-${key}`}
                                                                                options={ACCOUNT_TYPE_OPTIONS}
                                                                                value={product.account_type}
                                                                                onChange={(opt) => handleProductFieldChange(key, 'account_type', opt)}
                                                                                placeholder="Select type..."
                                                                                styles={customSelectStyles}
                                                                                classNamePrefix="react-select"
                                                                                isSearchable={false}
                                                                                isClearable={true}
                                                                            />
                                                                            {pErr?.account_type && (
                                                                                <span className="text-danger d-block mt-1" style={{ fontSize: "11px" }}>{pErr.account_type}</span>
                                                                            )}
                                                                        </Col>
                                                                        <Col sm="6" xs="12">
                                                                            <Label className="form-label fw-semibold text-dark small mb-1" htmlFor={`quarter-${key}`}>
                                                                                Quarter <span className="text-danger">*</span>
                                                                            </Label>
                                                                            <Select
                                                                                inputId={`quarter-${key}`}
                                                                                options={QUARTER_OPTIONS}
                                                                                value={product.quarter}
                                                                                onChange={(opt) => handleProductFieldChange(key, 'quarter', opt)}
                                                                                placeholder="Select quarter..."
                                                                                styles={customSelectStyles}
                                                                                classNamePrefix="react-select"
                                                                                isSearchable={false}
                                                                                isClearable={true}
                                                                            />
                                                                            {pErr?.quarter && (
                                                                                <span className="text-danger d-block mt-1" style={{ fontSize: "11px" }}>{pErr.quarter}</span>
                                                                            )}
                                                                        </Col>
                                                                        <Col sm="6" xs="12">
                                                                            <Label className="form-label fw-semibold text-dark small mb-1" htmlFor={`date-${key}`}>
                                                                                Stamping Date <span className="text-danger">*</span>
                                                                            </Label>
                                                                            {/* Only the months of the selected quarter are selectable */}
                                                                            <DatePicker
                                                                                id={`date-${key}`}
                                                                                className={`form-control form-control-sm ${pErr?.stamping_date || dateOutOfQuarter ? 'is-invalid' : ''}`}
                                                                                wrapperClassName="w-100"
                                                                                dateFormat="dd/MM/yyyy"
                                                                                placeholderText={product.quarter ? 'dd/mm/yyyy' : 'Select quarter first'}
                                                                                selected={product.stamping_date ? moment(product.stamping_date, 'YYYY-MM-DD').toDate() : null}
                                                                                onChange={(date) => handleProductFieldChange(key, 'stamping_date', date ? moment(date).format('YYYY-MM-DD') : '')}
                                                                                filterDate={(date) => isDateInQuarter(moment(date).format('YYYY-MM-DD'), product.quarter)}
                                                                                openToDate={!product.stamping_date && product.quarter
                                                                                    ? new Date(new Date().getFullYear(), getQuarterDateBounds(product.quarter).months[0] - 1, 1)
                                                                                    : undefined}
                                                                                disabled={!product.quarter}
                                                                                isClearable={Boolean(product.quarter)}
                                                                                showMonthDropdown
                                                                                showYearDropdown
                                                                                dropdownMode="select"
                                                                            />
                                                                            {pErr?.stamping_date ? (
                                                                                <span className="text-danger d-block mt-1" style={{ fontSize: "11px" }}>{pErr.stamping_date}</span>
                                                                            ) : dateOutOfQuarter ? (
                                                                                <span className="text-danger d-block mt-1" style={{ fontSize: "11px" }}>
                                                                                    Date must fall in {getQuarterDateBounds(product.quarter).label}
                                                                                </span>
                                                                            ) : product.quarter ? (
                                                                                <small className="text-muted d-block mt-1" style={{ fontSize: "11px" }}>
                                                                                    Allowed: <strong>{getQuarterDateBounds(product.quarter).label}</strong>
                                                                                </small>
                                                                            ) : null}
                                                                        </Col>
                                                                    </Row>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </Col>
                                                );
                                            })}
                                        </Row>
                                    )}
                                </>
                            )}
                        </div>

                        {/* SECTION 3: Account Location & Route Configuration */}
                        <div className="bg-white rounded-3 p-3 border mb-3 shadow-sm">
                            <div className="mb-3 pb-2 border-bottom">
                                {sectionTitle(<FaRoute size={16} />, '3. Route & Taluk')}
                            </div>

                            <Row className="g-3">
                                {/* Route Select with quick add (+) button */}
                                <Col md="6">
                                    <FormGroup className="mb-0">
                                        <Label htmlFor="route-select" className="form-label fw-semibold text-dark">
                                            Route
                                        </Label>
                                        <Row className="g-2 align-items-center">
                                            <Col xs="10">
                                                <Typeahead
                                                    id="route-select"
                                                    options={routeOptions}
                                                    value={selectedRoute}
                                                    onChange={(opt) => setSelectedRoute(opt)}
                                                    placeholder={loadingRoutes ? "Loading routes..." : "Select Route..."}
                                                    isLoading={loadingRoutes}
                                                    isClearable={true}
                                                />
                                            </Col>
                                            <Col xs="2" className="ps-1">
                                                <button
                                                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                                                    type="button"
                                                    style={{ height: "38px", borderRadius: "6px" }}
                                                    onClick={() => setRouteModal(true)}
                                                    title="Add New Route"
                                                >
                                                    +
                                                </button>
                                            </Col>
                                        </Row>
                                        {/* <small className="text-muted d-block mt-1">
                                            Assigned servicing route
                                        </small> */}
                                    </FormGroup>
                                </Col>

                                {/* Taluk Select with quick add (+) button */}
                                <Col md="6">
                                    <FormGroup className="mb-0">
                                        <Label htmlFor="taluk-select" className="form-label fw-semibold text-dark">
                                            Taluk
                                        </Label>
                                        <Row className="g-2 align-items-center">
                                            <Col xs="10">
                                                <Typeahead
                                                    id="taluk-select"
                                                    options={talukOptions}
                                                    value={selectedTaluk}
                                                    onChange={(opt) => setSelectedTaluk(opt)}
                                                    placeholder={loadingTaluks ? "Loading taluks..." : "Select Taluk..."}
                                                    isLoading={loadingTaluks}
                                                    isClearable={true}
                                                />
                                            </Col>
                                            <Col xs="2" className="ps-1">
                                                <button
                                                    className="btn btn-primary w-100 d-flex align-items-center justify-content-center"
                                                    type="button"
                                                    style={{ height: "38px", borderRadius: "6px" }}
                                                    onClick={() => setTalukModal(true)}
                                                    title="Add New Taluk"
                                                >
                                                    +
                                                </button>
                                            </Col>
                                        </Row>
                                        {/* <small className="text-muted d-block mt-1">
                                            Assigned administrative subdivision / taluk
                                        </small> */}
                                    </FormGroup>
                                </Col>

                                {/* Remarks */}

                            </Row>
                        </div>

                        {/* Review summary (shown before saving multiple products) */}
                        {isReviewing && selectedProductsCount > 0 && (
                            <div
                                id="stamping-review"
                                className="bg-white rounded-3 p-3 mb-3 shadow-sm"
                                style={{ border: '2px solid #0061cd' }}
                            >
                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2">
                                    <div>
                                        <div className="fw-bold text-dark">Review before saving</div>
                                        <small className="text-muted">
                                            {selectedProductsCount} products will be saved
                                            {selectedProducts.filter(p => p.original).length > 0 &&
                                                ` (${selectedProducts.filter(p => !p.original).length} new, ${selectedProducts.filter(p => p.original).length} updated)`}
                                            {selectedServiceAccount?.label ? ` for ${selectedServiceAccount.label}` : ''}.
                                        </small>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-light btn-sm d-inline-flex align-items-center gap-1"
                                        onClick={() => setIsReviewing(false)}
                                        disabled={submitting}
                                    >
                                        <FiArrowLeft size={13} /> Back to edit
                                    </button>
                                </div>
                                <div className="table-responsive">
                                    <table className="table table-sm align-middle mb-0" style={{ fontSize: "0.82rem" }}>
                                        <thead className="table-light">
                                            <tr>
                                                <th>Product</th>
                                                <th>Serial No.</th>
                                                <th>Account Type</th>
                                                <th>Quarter</th>
                                                <th>Stamping Date</th>
                                                <th>Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {selectedProducts.map(p => (
                                                <tr key={p.uniqueKey}>
                                                    <td className="fw-semibold">{p.product_name}</td>
                                                    <td>{p.machine_no || <span className="text-muted">—</span>}</td>
                                                    <td>{p.account_type?.label}</td>
                                                    <td>{p.quarter?.label}</td>
                                                    <td>{formatDisplayDate(p.stamping_date)}</td>
                                                    <td>{renderStatusBadge(p.stampStatus)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Save error (values are preserved) */}
                        {saveError && (
                            <div className="alert alert-danger d-flex align-items-start gap-2 py-2 px-3 mb-3" role="alert">
                                <FiAlertCircle size={16} className="mt-1 flex-shrink-0" />
                                <div className="small">
                                    <strong>Could not save.</strong> {saveError}
                                </div>
                                <button
                                    type="button"
                                    className="btn-close ms-auto"
                                    aria-label="Dismiss"
                                    onClick={() => setSaveError('')}
                                    style={{ fontSize: '0.7rem' }}
                                />
                            </div>
                        )}

                        {/* Form Submission Footer */}
                        <div className="py-3 px-3 px-md-4 bg-white d-flex justify-content-between align-items-center flex-wrap gap-2 rounded-3 border">
                            <div className="small">
                                {!selectedServiceAccount ? (
                                    <span className="text-muted">Select a customer and service account to start.</span>
                                ) : productsList.length === 0 ? (
                                    <span className="text-muted">No products available to stamp.</span>
                                ) : selectedProductsCount === 0 ? (
                                    <span className="d-flex align-items-center gap-1 text-warning fw-semibold">
                                        <FiAlertCircle size={14} />
                                        Select at least one product to stamp.
                                    </span>
                                ) : selectedIncompleteCount > 0 ? (
                                    <span className="d-flex align-items-center gap-1 fw-semibold" style={{ color: '#b45309' }}>
                                        <FiAlertCircle size={14} />
                                        {selectedIncompleteCount} of {selectedProductsCount} selected {selectedIncompleteCount === 1 ? 'product needs' : 'products need'} stamping details - click Edit to enter them.
                                    </span>
                                ) : (
                                    <span className="d-flex align-items-center gap-1 text-success fw-semibold">
                                        <FiCheckCircle size={14} />
                                        {selectedProductsCount} {selectedProductsCount === 1 ? 'product is' : 'products are'} ready to save.
                                    </span>
                                )}
                            </div>

                            <div className="d-flex gap-2 ms-auto">
                                <button
                                    type="button"
                                    className="btn btn-light px-3"
                                    onClick={() => navigate(-1)}
                                    disabled={submitting}
                                >
                                    Cancel
                                </button>
                                <Btn attrBtn={{
                                    color: "primary",
                                    type: "submit",
                                    disabled: primaryDisabled,
                                    className: "d-flex align-items-center gap-2 px-4 shadow-sm"
                                }}>
                                    {submitting && <Spinner size="sm" />}
                                    <span>{primaryLabel}</span>
                                </Btn>
                            </div>
                        </div>
                    </CardBody>
                </Form>

                {/* Quick Add Route Modal */}
                {routeModal && (
                    <AddEditRouteModal
                        modal={routeModal}
                        handleModal={() => setRouteModal(false)}
                        refreshList={(created) => {
                            fetchRouteOptions(created);
                        }}
                    />
                )}

                {/* Quick Add Taluk Modal */}
                {talukModal && (
                    <AddEditTalukModal
                        modal={talukModal}
                        handleModal={() => setTalukModal(false)}
                        refreshList={(created) => {
                            fetchTalukOptions(created);
                        }}
                    />
                )}
            </Card>
        </>
    );
};

export default AddEditStampingModal;
